import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Divider from '@mui/material/Divider';
import { LoaderButton } from '@skyslope/mache';

import { useDismissIntegrationDialog } from 'hooks/domain/integrations/useDismissIntegrationDialog';
import { useSetIntegrationEnablement } from 'hooks/domain/integrations/useSetIntegrationEnablement';
import { IntegrationKey } from 'resources/server/IntegrationsServer';
import { customHeapEvent } from 'utils/heap';

import { useRealSatisfiedOptIn, useShowIntegrationsDialog } from '../../hooks';
import type { StepProps } from '../../hooks/types';

import { RealSatisfiedCheckboxCard } from './RealSatisfiedCheckboxCard';
import { SellersShieldCheckboxCard } from './SellersShieldCheckboxCard';
import { StepCounter } from './StepCounter';
import { UtilityConnectCheckboxCard } from './UtilityConnectCheckboxCard';

export function IntegrationDialog({ currentStep, totalSteps }: StepProps) {
  const realSatisfied = useRealSatisfiedOptIn();
  const activateIntegration = useSetIntegrationEnablement();
  const dismissMutation = useDismissIntegrationDialog();
  const { integrationsToShow, refetchIntegrations } = useShowIntegrationsDialog();

  const [isActivating, setIsActivating] = React.useState(false);
  const [selectedIntegrations, setSelectedIntegrations] = React.useState<IntegrationKey[]>([]);

  React.useEffect(() => {
    setSelectedIntegrations(integrationsToShow);
  }, [integrationsToShow]);

  async function dismissIntegrations(integrations: IntegrationKey[]) {
    try {
      await dismissMutation.mutateAsync({ integrations });
      await refetchIntegrations();
      customHeapEvent(`Forms - Onboard Modal - Dismissed`);
    } catch (error) {
      console.error('Error dismissing integrations:', { integrations }, error);
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      setIsActivating(true);

      if (selectedIntegrations.includes(IntegrationKey.RealSatisfied)) {
        customHeapEvent('Forms - Onboard Modal - RealSatisfied Enabled');
        await realSatisfied.activateIntegration();
      }
      if (selectedIntegrations.includes(IntegrationKey.SellersShield)) {
        customHeapEvent('Forms - Onboard Modal - Sellers Shield Enabled');
        await activateIntegration.mutateAsync({
          integrationType: IntegrationKey.SellersShield,
          switchToEnablement: 'active',
        });
      }
      if (selectedIntegrations.includes(IntegrationKey.UtilityConnect)) {
        customHeapEvent('Forms - Onboard Modal - Utility Connect Enabled');
        await activateIntegration.mutateAsync({
          integrationType: IntegrationKey.UtilityConnect,
          switchToEnablement: 'active',
        });
      }

      const unselected = integrationsToShow.filter((integration) => !selectedIntegrations.includes(integration));
      if (unselected.length) dismissIntegrations(unselected);
    } catch (error) {
      console.error('Error activating integrations:', { selectedIntegrations }, error);
    } finally {
      setIsActivating(false);
    }
  }

  function handleChange(integration: IntegrationKey, checked: boolean) {
    if (checked) {
      setSelectedIntegrations((prev) => [...prev, integration]);
    } else {
      setSelectedIntegrations((prev) => prev.filter((item) => item !== integration));
    }
  }

  return (
    <Dialog open maxWidth={'sm'} classes={{ paper: 'rounded-xl px-5 pt-10 pb-5' }}>
      <DialogTitle className="py-0 mb-8 text-2xl font-medium text-black">
        <StepCounter currentStep={currentStep} totalSteps={totalSteps} />
        Activate performance-boosting integrations.
      </DialogTitle>
      <form className="overflow-x-hidden" onSubmit={handleSubmit}>
        <DialogContent className="flex flex-col gap-5">
          {integrationsToShow.includes(IntegrationKey.UtilityConnect) && (
            <UtilityConnectCheckboxCard
              disabled={isActivating}
              onChange={(e, checked) => handleChange(IntegrationKey.UtilityConnect, checked)}
              checked={selectedIntegrations.includes(IntegrationKey.UtilityConnect)}
            />
          )}
          {integrationsToShow.includes(IntegrationKey.RealSatisfied) && (
            <RealSatisfiedCheckboxCard
              disabled={isActivating}
              onChange={(e, checked) => handleChange(IntegrationKey.RealSatisfied, checked)}
              checked={selectedIntegrations.includes(IntegrationKey.RealSatisfied)}
            />
          )}
          {integrationsToShow.includes(IntegrationKey.SellersShield) && (
            <SellersShieldCheckboxCard
              disabled={isActivating}
              onChange={(e, checked) => handleChange(IntegrationKey.SellersShield, checked)}
              checked={selectedIntegrations.includes(IntegrationKey.SellersShield)}
            />
          )}
        </DialogContent>
        <Divider className="mx-[-2.5rem]" />
        <DialogActions className="px-0 pt-5 pb-0">
          <LoaderButton
            type="button"
            variant="contained"
            color="secondary"
            onClick={() => dismissIntegrations(integrationsToShow)}
            className="transition-none"
            isLoading={dismissMutation.isLoading}
          >
            Dismiss
          </LoaderButton>

          <LoaderButton
            type="submit"
            className="ml-2 transition-none"
            variant="contained"
            color="primary"
            isLoading={isActivating}
            disabled={selectedIntegrations.length === 0}
          >
            Activate
          </LoaderButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
